// pdf.js's legacy build still needs these polyfills. Listing the import first
// does not guarantee it runs before pdf.js: `manualChunks` splits pdfjs-dist
// into its own chunk, which may evaluate first. That's fine only because pdf.js
// uses the polyfilled features at call time, not at module load.
import './pdfjsPolyfills';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Typography from '@lib/components/Typography/Typography';
import downloadHandler from '@lib/utils/downloadHandler';
import isMobile from '@lib/utils/isMobile';
import useElementDimensions from '@lib/hooks/useElementDimensions';
import { logger } from '@lib/services/logger';
import { getPendingPage, isPageInDocument } from './utils';
import HighlightedPage from './HighlightedPage';
import { PdfPageHighlight } from './types';
// Bundle the pdfjs worker locally so it is served same-origin (the previous
// CDN URL was blocked by CORS). pdfjs-dist is pinned to the exact version
// `react-pdf` bundles internally, since the API and worker versions must
// match. Both API and worker come from the polyfilled `legacy` build so older
// browsers can render PDFs (see `lib/src/utils/vitePdfjsLegacy.mts`); the
// worker is wrapped in `pdf.worker.ts` to add the missing polyfills.
import workerUrl from './pdf.worker?worker&url';
import styles from './PDFViewer.module.scss';

// Set at module scope, not in a useEffect: <Document> kicks off loading the
// PDF from its own child effect, which runs before this component's effects
// (React fires child effects before parent effects), so a useEffect here
// would race it and the worker could still be the react-pdf default.
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

// Image decoders (JBIG2/CCITTFax in scanned PDFs, JPEG 2000), CMaps, standard
// fonts and the CMYK ICC profile, served by `lib/src/utils/vitePdfjsAssets.mts`. Module-level
// constant: react-pdf reloads the document whenever `options` changes identity.
const PDFJS_ASSETS_URL = new URL(
  `${import.meta.env.BASE_URL}pdfjs/${pdfjs.version}/`,
  // Not `location.origin`: it ignores a relative base and is `'null'` (which
  // makes `new URL` throw) on opaque origins.
  document.baseURI,
).href;
const DOCUMENT_OPTIONS = {
  wasmUrl: `${PDFJS_ASSETS_URL}wasm/`,
  cMapUrl: `${PDFJS_ASSETS_URL}cmaps/`,
  standardFontDataUrl: `${PDFJS_ASSETS_URL}standard_fonts/`,
  iccUrl: `${PDFJS_ASSETS_URL}iccs/`,
};

const ZOOM_STEP = 0.2;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 4;

interface Props {
  fileUrl: string;
  downloadUrl?: string;
  onFullScreenToggle?: () => void;
  isFullScreen?: boolean;
  useDownloadTool?: boolean;
  usePrintTool?: boolean;
  useFavoriteTool?: boolean;
  onFavoriteToggle?: () => void;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
  // 1-based page to open the document at (e.g. a verified AI page citation).
  // Read when a file opens; defaults to the first page.
  initialPage?: number;
  // Passages to highlight, by page (e.g. a verified AI citation's quote). Read
  // when a file opens, like `initialPage`; the viewer lands on the first
  // highlight of the page it opens at.
  highlights?: PdfPageHighlight[];
}

// Prints the raw PDF via the browser's native PDF viewer instead of the
// rendered canvas. Unlike ImageViewer's print (a hidden iframe holding a
// plain `document.write`-generated HTML page, which prints fine via
// `contentWindow.print()`), a PDF's rendering is handed off to Chrome's
// built-in PDF viewer plugin, and *that* doesn't honor `contentWindow.print()`
// from any ancestor frame — confirmed by direct testing across a same-origin
// `blob:` src, every size/timing, and even a plain host document with the PDF
// nested inside it via <embed> one level down. Only a genuine top-level
// `window.print()` reliably opens the dialog, so the PDF has to actually be
// the tab's own document — hence a new tab instead of an iframe.
//
// The tab is opened synchronously (before the `fetch`) so the click that
// triggered this still counts as the user gesture `window.open` needs —
// opening it only after the async fetch resolves gets silently popup-blocked.
function printFile(fileUrl: string) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.warn('printFile: print window was blocked');
    return;
  }

  (async () => {
    let objectUrl: string;
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      objectUrl = URL.createObjectURL(blob);
    } catch (error) {
      console.warn(error);
      printWindow.close();
      return;
    }

    printWindow.addEventListener('load', () => {
      printWindow.focus();
      printWindow.print();
    });
    printWindow.addEventListener('unload', () => {
      URL.revokeObjectURL(objectUrl);
    });
    printWindow.location.href = objectUrl;
  })();
}

export default function PDFViewer(props: Props) {
  const {
    fileUrl,
    downloadUrl,
    onFullScreenToggle,
    isFullScreen,
    useDownloadTool = true,
    usePrintTool = true,
    useFavoriteTool = false,
    onFavoriteToggle,
    isFavorited,
    isFavoriteLoading,
    initialPage,
    highlights,
  } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  // Pages get mounted (and their canvas rendered) once they scroll near the
  // viewport, not all at once — a multi-hundred-page document would otherwise
  // render every canvas up front, stalling the browser and leaving newly
  // navigated-to pages blank while still mid-render. Never removed once
  // added, so already-rendered pages don't disappear as the user scrolls on.
  const [loadedPages, setLoadedPages] = useState<Set<number>>(
    () => new Set([1]),
  );
  // Rendered height of a loaded page, reused as the placeholder height for
  // not-yet-loaded pages so the scrollbar/scroll position stay stable instead
  // of jumping around as pages lazily mount.
  const [pageHeight, setPageHeight] = useState<number | null>(null);
  const { phone, tablet } = isMobile();
  const isFitToWidth = phone || tablet;

  const viewerWrapRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { dimensions } = useElementDimensions(
    viewerWrapRef,
    String(isFullScreen),
  );

  // Page still waiting to be scrolled to (`initialPage`), consumed once per
  // file by the first jump below. Null when the viewer should just open at the
  // top.
  const pendingPageRef = useRef<number | null>(null);
  // Set while a previous/next click smooth-scrolls to its page. The scroll
  // passes back over the page it started from, so the observer below would
  // briefly report it again (2 → 1 → 2); the counter keeps the clicked page
  // until the scroll settles instead.
  const isNavigatingRef = useRef(false);
  const navigationSettleTimerRef = useRef<number | undefined>(undefined);
  // Page the viewer landed on via `initialPage`. Pages around it keep
  // rendering (and changing height) after the jump — mixed page sizes, a late
  // fit-to-width, placeholders above mounting — so every page render
  // re-aligns to it until the user scrolls or navigates on their own.
  const landingPageRef = useRef<number | null>(null);
  // The highlight on the landing page the viewer keeps aligned to instead of
  // the page top, once its text layer has rendered.
  const landingElementRef = useRef<HTMLElement | null>(null);
  // Page whose first highlight the viewer lands on — the page it opens at.
  const highlightTargetPageRef = useRef<number | null>(null);

  const toHighlightsByPage = (pageHighlights?: PdfPageHighlight[]) =>
    new Map(pageHighlights?.map((highlight) => [highlight.page, highlight]));
  const [highlightsByPage, setHighlightsByPage] = useState(() =>
    toHighlightsByPage(highlights),
  );

  useEffect(() => {
    setPageNumber(1);
    setZoom(1);
    setLoadedPages(new Set([1]));
    setPageHeight(null);
    pendingPageRef.current = getPendingPage(initialPage);
    isNavigatingRef.current = false;
    landingPageRef.current = null;
    landingElementRef.current = null;
    highlightTargetPageRef.current = highlights?.length
      ? (getPendingPage(initialPage) ?? 1)
      : null;
    setHighlightsByPage(toHighlightsByPage(highlights));
    // `initialPage` and `highlights` are read only when a file opens —
    // changing them on an open file is not a navigation request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUrl]);

  // Reset to the fit/default zoom whenever the viewer enters or exits
  // fullscreen, mirroring the previous zoomTo(defaultScale) behaviour.
  useEffect(() => {
    setZoom(1);
  }, [isFullScreen]);

  const handleDocumentLoadSuccess = useCallback((pdf: { numPages: number }) => {
    setNumPages(pdf.numPages);
    setIsLoading(false);
    const pendingPage = pendingPageRef.current;
    if (!pendingPage) return;
    // A page past the end (never expected from a verified citation, so it
    // points at wrong citation or file data) just leaves the viewer at the top.
    if (!isPageInDocument(pendingPage, pdf.numPages)) {
      logger.warn('PDFViewer: initial page is past the last page', {
        initialPage: pendingPage,
        numPages: pdf.numPages,
      });
      pendingPageRef.current = null;
      return;
    }
    // Mount the target page up front so it isn't a blank placeholder when the
    // viewer lands on it.
    setLoadedPages((prev) => new Set(prev).add(pendingPage));
  }, []);

  // All pages render at once in a scrollable container (see below), so the
  // "current page" is whichever page is most visible in the viewport, not a
  // single rendered page. Track it with an observer instead of state driving
  // which page renders.
  //
  // The observer only triggers a recompute here — picking the page itself is
  // done by scanning `pageRefs` geometrically rather than trusting whichever
  // entries the observer happens to hand back. Picking by highest
  // `intersectionRatio` breaks whenever the container is tall enough to show
  // several pages fully at once (e.g. fullscreen mode): they all tie at 1.0,
  // and which one "wins" depends on unspecified entry ordering — that's what
  // produced the wrong initial page number in fullscreen. Scanning in page
  // order for the last page whose top has crossed the container's midline is
  // deterministic regardless of how many pages fit in view at once.
  useEffect(() => {
    const container = viewerWrapRef.current;
    if (!container || numPages === 0) return undefined;

    const updateCurrentPage = () => {
      if (isNavigatingRef.current) return;
      const containerRect = container.getBoundingClientRect();
      const midline = containerRect.top + containerRect.height / 2;
      let current = 1;
      for (let i = 0; i < pageRefs.current.length; i += 1) {
        const page = pageRefs.current[i];
        if (!page) break;
        if (page.getBoundingClientRect().top > midline) break;
        current = i + 1;
      }
      setPageNumber(current);
    };

    const observer = new IntersectionObserver(updateCurrentPage, {
      root: container,
      threshold: [0.25, 0.5, 0.75],
    });
    pageRefs.current.forEach((page) => page && observer.observe(page));

    return () => observer.disconnect();
  }, [numPages, fileUrl]);

  // Separate observer, with a generous rootMargin, drives which pages are
  // mounted at all (see `loadedPages` above) — deliberately loose so pages
  // are ready just before they're scrolled to, unlike the tight thresholds
  // the "current page" observer above needs for an accurate counter.
  useEffect(() => {
    const container = viewerWrapRef.current;
    if (!container || numPages === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const pagesToLoad = entries
          .filter((entry) => entry.isIntersecting)
          .map(
            (entry) =>
              pageRefs.current.indexOf(entry.target as HTMLDivElement) + 1,
          )
          .filter((page) => page > 0);
        if (pagesToLoad.length === 0) return;
        setLoadedPages((prev) => {
          if (pagesToLoad.every((page) => prev.has(page))) return prev;
          const next = new Set(prev);
          pagesToLoad.forEach((page) => next.add(page));
          return next;
        });
      },
      { root: container, rootMargin: '100% 0px', threshold: 0 },
    );
    pageRefs.current.forEach((page) => page && observer.observe(page));

    return () => observer.disconnect();
  }, [numPages, fileUrl]);

  // `pageNumber` state lags behind a click: it's only corrected once the
  // smooth-scroll finishes and the IntersectionObserver above reports back,
  // which is asynchronous. Reading it in the click handlers would make a
  // second click (before that round-trip completes) re-target the same page
  // instead of advancing. This ref is updated synchronously on every click
  // and kept in sync with the observer's updates, so consecutive clicks
  // always advance from the last requested page.
  const currentPageRef = useRef(1);
  useEffect(() => {
    currentPageRef.current = pageNumber;
  }, [pageNumber]);

  // Aligns the viewer to where it landed: the landing highlight (centred)
  // once it has rendered, else the landing page's top.
  const realignToLandingPage = useCallback(() => {
    const landingPage = landingPageRef.current;
    if (!landingPage) return;
    const landingElement = landingElementRef.current;
    if (landingElement?.isConnected) {
      landingElement.scrollIntoView({ block: 'center' });
      return;
    }
    pageRefs.current[landingPage - 1]?.scrollIntoView({ block: 'start' });
  }, []);

  // Lands on `initialPage` once the layout is measurable: the placeholders
  // above the target only get their height after the first page renders
  // (`pageHeight`), so scrolling earlier would stop short. Instant, not
  // smooth — the user asked to open AT that page, not to watch a scroll.
  useEffect(() => {
    const pendingPage = pendingPageRef.current;
    if (!pendingPage || numPages === 0 || pageHeight === null) return;
    const target = pageRefs.current[pendingPage - 1];
    if (!target) return;
    pendingPageRef.current = null;
    landingPageRef.current = pendingPage;
    currentPageRef.current = pendingPage;
    setPageNumber(pendingPage);
    realignToLandingPage();
  }, [numPages, pageHeight, realignToLandingPage]);

  // A page's highlights rendered: land on the first one when it's on the page
  // the viewer opened at (and the user hasn't taken over scrolling).
  const handleHighlightReady = useCallback(
    (page: number, element: HTMLElement) => {
      if (page !== highlightTargetPageRef.current) return;
      landingPageRef.current = page;
      landingElementRef.current = element;
      realignToLandingPage();
    },
    [realignToLandingPage],
  );

  const handlePageLoadSuccess = useCallback(
    (loadedPage: { height: number }) => setPageHeight(loadedPage.height),
    [],
  );

  const endNavigation = useCallback(() => {
    window.clearTimeout(navigationSettleTimerRef.current);
    isNavigatingRef.current = false;
  }, []);

  // A previous/next scroll counts as settled once no scroll event has fired
  // for a moment (`scrollend` is missing from older Safari). Also armed by the
  // click itself, for when the page is already in place and nothing scrolls.
  const scheduleNavigationEnd = useCallback(() => {
    window.clearTimeout(navigationSettleTimerRef.current);
    navigationSettleTimerRef.current = window.setTimeout(endNavigation, 150);
  }, [endNavigation]);

  useEffect(() => {
    const container = viewerWrapRef.current;
    if (!container) return undefined;
    const handleScroll = () => {
      if (isNavigatingRef.current) scheduleNavigationEnd();
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      endNavigation();
    };
  }, [scheduleNavigationEnd, endNavigation]);

  // Any user input in the document hands scrolling back to the user.
  useEffect(() => {
    const container = viewerWrapRef.current;
    if (!container) return undefined;
    const release = () => {
      landingPageRef.current = null;
      landingElementRef.current = null;
      highlightTargetPageRef.current = null;
      endNavigation();
    };
    const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    events.forEach((event) =>
      container.addEventListener(event, release, { passive: true }),
    );
    return () =>
      events.forEach((event) => container.removeEventListener(event, release));
  }, [endNavigation]);

  const scrollToPage = (page: number) => {
    landingPageRef.current = null;
    landingElementRef.current = null;
    highlightTargetPageRef.current = null;
    currentPageRef.current = page;
    setPageNumber(page);
    isNavigatingRef.current = true;
    scheduleNavigationEnd();
    pageRefs.current[page - 1]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const goToPreviousPage = () =>
    scrollToPage(Math.max(1, currentPageRef.current - 1));
  const goToNextPage = () =>
    scrollToPage(Math.min(numPages, currentPageRef.current + 1));
  const zoomIn = () =>
    setZoom((scale) => Math.min(MAX_ZOOM, scale + ZOOM_STEP));
  const zoomOut = () =>
    setZoom((scale) => Math.max(MIN_ZOOM, scale - ZOOM_STEP));

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.actionsRow}>
          <div className={styles.actionsRow}>
            <IconButton
              icon="navigate_before"
              onClick={goToPreviousPage}
              symbolsOutlined
              disabled={isLoading || pageNumber <= 1}
            />
            <Typography variant="body">
              {isLoading ? 0 : pageNumber} / {numPages}
            </Typography>
            <IconButton
              icon="navigate_next"
              onClick={goToNextPage}
              symbolsOutlined
              disabled={isLoading || pageNumber >= numPages}
            />
          </div>
          {onFullScreenToggle && (
            <IconButton
              icon={isFullScreen ? 'fullscreen_exit' : 'fullscreen'}
              onClick={onFullScreenToggle}
              symbolsOutlined
              disabled={isLoading}
            />
          )}
          <IconButton
            icon="zoom_in"
            onClick={zoomIn}
            symbolsOutlined
            disabled={isLoading}
          />
          <IconButton
            icon="zoom_out"
            onClick={zoomOut}
            symbolsOutlined
            disabled={isLoading}
          />
        </div>
        <div className={styles.actionsRow}>
          {usePrintTool && (
            <IconButton
              icon="print"
              onClick={() => {
                printFile(fileUrl);
              }}
              symbolsOutlined
              disabled={isLoading}
            />
          )}
          {useDownloadTool && (
            <IconButton
              icon="download"
              onClick={() => downloadHandler(downloadUrl)}
              symbolsOutlined
              disabled={isLoading}
            />
          )}
          {useFavoriteTool && onFavoriteToggle && (
            <IconButton
              icon={isFavorited ? 'bookmark_star' : 'bookmark'}
              onClick={onFavoriteToggle}
              symbolsOutlined
              filled={isFavorited}
              isLoading={isFavoriteLoading}
              type={IconButtonTypes.PrimaryOutlined}
              disabled={isLoading}
            />
          )}
        </div>
      </div>
      <div
        ref={viewerWrapRef}
        className={isFullScreen ? styles.viewerWrapFull : styles.viewerWrap}
      >
        <Document
          file={fileUrl}
          options={DOCUMENT_OPTIONS}
          onLoadSuccess={handleDocumentLoadSuccess}
          loading=""
          // react-pdf 11 suspends by default; keep the in-place loading so the
          // toolbar stays mounted instead of bubbling to the app's boundary.
          suspense={false}
          className={styles.document}
        >
          {Array.from({ length: numPages }, (_, index) => {
            const page = index + 1;
            const isPageLoaded = loadedPages.has(page);
            return (
              <div
                key={page}
                className={styles.pageWrap}
                style={
                  !isPageLoaded && pageHeight
                    ? { height: pageHeight }
                    : undefined
                }
                ref={(el) => {
                  pageRefs.current[index] = el;
                }}
              >
                {isPageLoaded && (
                  <HighlightedPage
                    pageNumber={page}
                    scale={zoom}
                    width={
                      isFitToWidth && dimensions?.width
                        ? dimensions.width
                        : undefined
                    }
                    highlight={highlightsByPage.get(page)}
                    onLoadSuccess={handlePageLoadSuccess}
                    onRenderSuccess={realignToLandingPage}
                    onHighlightReady={handleHighlightReady}
                  />
                )}
              </div>
            );
          })}
        </Document>
      </div>
    </div>
  );
}
